import React, { useMemo, useState, useEffect } from "react";
import { Check, Repeat, MoreHorizontal, SkipForward, X } from "lucide-react";

// Per-exercise card with per-set slots, tap-to-log.
// Props:
// - ex: exercise object from program
// - state: { sets: [{w, reps, logged, skipped}], skippedEntirely }
// - onChange(state)
// - isFirstIncomplete: only this card's active element pulses
// - onReplace, onSkip
export default function ExerciseCard({ ex, state, onChange, isFirstIncomplete, onReplace, onSkip, onSetLogged, unit = "kg" }) {
  const [showMenu, setShowMenu] = useState(false);
  const [entryW, setEntryW] = useState("");
  const [entryR, setEntryR] = useState("");
  const [err, setErr] = useState("");

  const displayW = ex.displayWeight ?? ex.weight;
  const isBlank = ex.enc === 0;
  const isPredicted = ex.enc === 1;
  const isValidated = ex.enc >= 2;

  useEffect(() => {
    if (!isBlank && displayW) {
      setEntryW(String(displayW));
      setEntryR(String(ex.reps));
    }
  }, [displayW, ex.reps, isBlank]);

  const logged = state.sets.filter((s) => s.logged && !s.skipped).length;
  const totalSets = ex.sets;
  const allLogged = logged >= totalSets && !state.skippedEntirely;

  const capW = ex.anchor === "legs" ? 600 : 300;

  function validate(w, r) {
    const wn = Number(w), rn = Number(r);
    if (!Number.isFinite(wn) || wn <= 0) return "Weight must be > 0";
    if (wn > capW) return `Max ${capW} kg for ${ex.anchor}`;
    if (!Number.isInteger(rn) || rn < 1) return "Reps must be ≥ 1";
    if (rn > 40) return "Max 40 reps";
    return "";
  }

  function logSet(i, wOverride, rOverride) {
    const w = wOverride ?? (isBlank ? entryW : (state.sets[i]?.w ?? displayW));
    const r = rOverride ?? (isBlank ? entryR : (state.sets[i]?.reps ?? ex.reps));
    const msg = validate(w, r);
    if (msg) { setErr(msg); return; }
    setErr("");
    const next = { ...state, sets: state.sets.map((s, idx) => idx === i ? { ...s, w: Number(w), reps: Number(r), logged: true, skipped: false } : s) };
    onChange(next);
    if (onSetLogged) onSetLogged({ exercise: ex, setIndex: i });
  }

  function skipSet(i) {
    onChange({ ...state, sets: state.sets.map((s, idx) => idx === i ? { ...s, skipped: true, logged: false } : s) });
  }

  function completeAll() {
    const w = Number(entryW || displayW);
    const r = Number(entryR || ex.reps);
    const msg = validate(w, r);
    if (msg) { setErr(msg); return; }
    onChange({ ...state, sets: state.sets.map((s) => ({ ...s, w, reps: r, logged: true, skipped: false })) });
  }

  const firstUnlogged = state.sets.findIndex((s) => !s.logged && !s.skipped);

  return (
    <div className={`card p-5 ${allLogged ? "completed" : ""}`} data-testid={`ex-card-${ex.exerciseKey}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display text-xl tracking-tight uppercase">{ex.name}</h3>
            {ex.main && <span className="overline">Main</span>}
          </div>
          <div className="mt-1 text-xs text-t3">
            {totalSets} sets × {ex.reps} reps · rest {ex.rest}s{ex.each ? " · per arm" : ""}{ex.timed ? " · time" : ""}
          </div>
          {isPredicted && ex.lastLog && (
            <div className="mt-2 text-xs text-amber pulse-slow-text" data-testid={`ex-pred-${ex.exerciseKey}`}>
              Predicted from last time · {ex.lastLog.w} {unit}{ex.each ? " each" : ""} × {ex.lastLog.reps} avg
            </div>
          )}
          {ex.deloaded && (
            <div className="mt-2 inline-flex items-center gap-2" data-testid={`ex-deload-${ex.exerciseKey}`}>
              <span className="text-[10px] uppercase tracking-[0.22em] px-2 py-1 rounded-full" style={{ background: "rgba(184,134,11,0.12)", color: "var(--amber)", border: "1px solid rgba(184,134,11,0.35)" }}>Deload</span>
              <span className="text-xs text-t3">60% of {ex.weight ?? "—"} {unit}</span>
            </div>
          )}
        </div>
        <button className="stepper" onClick={() => setShowMenu((v) => !v)} data-testid={`ex-menu-${ex.exerciseKey}`} aria-label="Menu">
          <MoreHorizontal size={18} />
        </button>
      </div>

      {showMenu && (
        <div className="mt-3 flex flex-wrap gap-2">
          {ex.subs?.length > 0 && (
            <button className="btn btn-outline text-xs" onClick={() => { setShowMenu(false); onReplace(ex); }} data-testid={`ex-replace-${ex.exerciseKey}`}>
              <Repeat size={14} /> Replace
            </button>
          )}
          <button className="btn btn-outline text-xs" onClick={() => { setShowMenu(false); onSkip(ex); }} data-testid={`ex-skip-${ex.exerciseKey}`}>
            <SkipForward size={14} /> Skip today
          </button>
          {!isBlank && !allLogged && (
            <button className="btn btn-outline text-xs" onClick={() => { setShowMenu(false); completeAll(); }} data-testid={`ex-complete-${ex.exerciseKey}`}>
              <Check size={14} /> Complete all
            </button>
          )}
        </div>
      )}

      {isBlank && (
        <div className="mt-4 grid grid-cols-2 gap-3">
          <div>
            <label className="overline">Weight ({unit}{ex.each ? " each" : ""})</label>
            <input inputMode="decimal" type="number" step="0.5" min="2.5" value={entryW}
              onChange={(e) => setEntryW(e.target.value)}
              className={`input mt-2 ${isFirstIncomplete && !entryW ? "pulse-slow" : ""} ${err ? "input-error" : ""}`}
              placeholder="—" data-testid={`ex-weight-${ex.exerciseKey}`} />
          </div>
          <div>
            <label className="overline">Reps</label>
            <input inputMode="numeric" type="number" min="1" value={entryR}
              onChange={(e) => setEntryR(e.target.value)}
              className={`input mt-2 ${isFirstIncomplete && entryW && !entryR ? "pulse-slow" : ""} ${err ? "input-error" : ""}`}
              placeholder="—" data-testid={`ex-reps-${ex.exerciseKey}`} />
          </div>
        </div>
      )}

      <div className="mt-4 space-y-2">
        {state.sets.map((s, i) => {
          const activeSlot = !s.logged && !s.skipped && firstUnlogged === i && isFirstIncomplete && !isBlank;
          const activeBlankSlot = !s.logged && !s.skipped && firstUnlogged === i && isFirstIncomplete && isBlank && entryW && entryR;
          const cls = `set-slot ${s.logged ? "logged" : ""} ${(activeSlot || activeBlankSlot) ? "pulse-slow" : ""}`;
          return (
            <div key={`${ex.exerciseKey}-set-${i}`} className={cls} data-testid={`set-${ex.exerciseKey}-${i}`}>
              <span className="overline">Set {i + 1}</span>
              {s.skipped ? (
                <span className="text-t3 text-sm">Skipped</span>
              ) : s.logged ? (
                <span className="font-mono tabular text-lime">{s.w} {unit}{ex.each ? "/side" : ""} × {s.reps}</span>
              ) : (
                <span className={`font-mono tabular ${isPredicted ? "text-amber" : "text-t2"}`}>
                  {isBlank ? "— × —" : `${displayW} ${unit} × ${ex.reps}`}
                </span>
              )}
              <div className="flex gap-1">
                {!s.logged && !s.skipped && (
                  <>
                    <button className="btn btn-ghost text-xs" onClick={() => skipSet(i)} data-testid={`skip-set-${ex.exerciseKey}-${i}`}>
                      <X size={14} />
                    </button>
                    <button className="btn bg-lime text-xs" style={{ padding: "6px 12px", borderRadius: 999 }}
                      onClick={() => logSet(i)} data-testid={`log-set-${ex.exerciseKey}-${i}`}>
                      Log
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {err && <div className="mt-2 text-xs text-red" data-testid={`ex-err-${ex.exerciseKey}`}>{err}</div>}
    </div>
  );
}
